import { useCallback, useEffect, useRef, useState } from "react";
import { Lightbox } from "./Lightbox";
import type { Project, ProjectImage, ProjectVideo } from "../types/project";

interface MediaShowcaseProps {
  project: Project;
}

type MediaItem =
  | { kind: "video"; video: ProjectVideo }
  | { kind: "image"; image: ProjectImage; imageIndex: number };

const ADVANCE_MS = 5000;

const ARROW =
  "absolute top-1/2 z-10 grid size-10 -translate-y-1/2 cursor-pointer place-items-center border border-zinc-700 bg-black/70 font-mono text-base text-zinc-300 transition-colors hover:border-zinc-100 hover:bg-zinc-100 hover:text-black focus-visible:border-zinc-100 focus-visible:bg-zinc-100 focus-visible:text-black focus-visible:outline-none sm:size-12";

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function youtubeThumbnail(video: ProjectVideo) {
  return `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`;
}

export function MediaShowcase({ project }: MediaShowcaseProps) {
  const images = [project.key, ...(project.gallery ?? [])];
  const items: MediaItem[] = [
    ...(project.videos ?? []).map((video) => ({ kind: "video" as const, video })),
    ...images.map((image, imageIndex) => ({ kind: "image" as const, image, imageIndex })),
  ];
  const count = items.length;

  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [autoplay, setAutoplay] = useState(() => !prefersReducedMotion());
  const [held, setHeld] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActive(0);
    setPlaying(false);
    setLightbox(null);
  }, [project.slug]);

  useEffect(() => {
    if (!autoplay || held || playing || lightbox !== null || count < 2) return;

    const timer = window.setTimeout(
      () => setActive((current) => (current + 1) % count),
      ADVANCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [active, autoplay, held, playing, lightbox, count]);

  useEffect(() => {
    const strip = stripRef.current;
    const thumb = strip?.children[active] as HTMLElement | undefined;
    if (!strip || !thumb) return;

    strip.scrollTo({
      left: thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [active]);

  const select = useCallback((index: number) => {
    setAutoplay(false);
    setPlaying(false);
    setActive(index);
  }, []);

  const step = (delta: number) => select((active + delta + count) % count);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const stepLightbox = useCallback(
    (delta: number) =>
      setLightbox((current) =>
        current === null ? current : (current + delta + images.length) % images.length,
      ),
    [images.length],
  );

  const current = items[active];
  if (!current) return null;

  return (
    <section
      aria-label={`${project.title} media`}
      className="border-b border-zinc-800 py-10"
    >
      <div
        className="relative aspect-video overflow-hidden border border-zinc-800 bg-black"
        onMouseEnter={() => setHeld(true)}
        onMouseLeave={() => setHeld(false)}
      >
        {current.kind === "image" ? (
          <button
            type="button"
            onClick={() => {
              setAutoplay(false);
              setLightbox(current.imageIndex);
            }}
            aria-label={`Enlarge image — ${current.image.alt}`}
            className="block size-full cursor-zoom-in focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-4 focus-visible:outline-zinc-100"
          >
            <img
              src={current.image.src}
              alt={current.image.alt}
              decoding="async"
              className="block size-full object-contain"
            />
          </button>
        ) : playing ? (
          <iframe
            key={current.video.youtubeId}
            src={`https://www.youtube-nocookie.com/embed/${current.video.youtubeId}?autoplay=1&rel=0`}
            title={current.video.title}
            className="absolute inset-0 size-full border-0"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setAutoplay(false);
              setPlaying(true);
            }}
            aria-label={`Play video — ${current.video.title}`}
            className="group block size-full cursor-pointer focus-visible:outline-none"
          >
            <img
              src={youtubeThumbnail(current.video)}
              alt=""
              decoding="async"
              className="block size-full object-cover opacity-80 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border border-zinc-100 bg-black/75 px-5 py-3 font-mono text-[10px] tracking-[0.2em] text-zinc-100 transition-colors group-hover:bg-zinc-100 group-hover:text-black group-focus-visible:bg-zinc-100 group-focus-visible:text-black">
              ▶ PLAY VIDEO
            </span>
          </button>
        )}

        {count > 1 ? (
          <>
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous media"
              className={`${ARROW} left-2 sm:left-3`}
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next media"
              className={`${ARROW} right-2 sm:right-3`}
            >
              ›
            </button>
          </>
        ) : null}
      </div>

      {count > 1 ? (
        <div
          ref={stripRef}
          className="relative mt-2 flex gap-2 overflow-x-auto pb-2"
          aria-label="Media thumbnails"
          role="group"
        >
          {items.map((item, index) => {
            const isActive = index === active;
            const label =
              item.kind === "video"
                ? `Show video — ${item.video.title}`
                : `Show image — ${item.image.alt}`;

            return (
              <button
                key={item.kind === "video" ? item.video.youtubeId : item.image.src}
                type="button"
                onClick={() => select(index)}
                aria-label={label}
                aria-current={isActive ? "true" : undefined}
                className={`relative aspect-video w-28 shrink-0 cursor-pointer overflow-hidden border bg-black transition-[border-color,opacity] focus-visible:border-zinc-100 focus-visible:opacity-100 focus-visible:outline-none sm:w-36 ${
                  isActive
                    ? "border-zinc-100 opacity-100"
                    : "border-zinc-800 opacity-55 hover:border-zinc-500 hover:opacity-100"
                }`}
              >
                <img
                  src={item.kind === "video" ? youtubeThumbnail(item.video) : item.image.src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="block size-full object-cover"
                />
                {item.kind === "video" ? (
                  <span className="absolute inset-0 grid place-items-center bg-black/35 font-mono text-xs text-zinc-100">
                    ▶
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="mt-2 flex items-center justify-between gap-4 font-mono text-[8px] tracking-[0.18em] text-zinc-700">
        <span>
          MEDIA // {String(active + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
        </span>
        {count > 1 ? (
          <button
            type="button"
            onClick={() => setAutoplay((on) => !on)}
            aria-pressed={autoplay}
            className="cursor-pointer border border-zinc-800 px-2.5 py-1.5 text-zinc-500 transition-colors hover:border-zinc-100 hover:text-zinc-100 focus-visible:border-zinc-100 focus-visible:text-zinc-100 focus-visible:outline-none"
          >
            AUTOPLAY {autoplay ? "ON" : "OFF"}
          </button>
        ) : null}
      </div>

      {lightbox !== null && images[lightbox] ? (
        <Lightbox
          images={images}
          index={lightbox}
          onClose={closeLightbox}
          onStep={stepLightbox}
        />
      ) : null}
    </section>
  );
}
